import { Button, Input } from "antd";
import React, { useEffect, useState } from "react";
import styles from "./styles.module.css";
import { loginApi, verify2faOtpApi, verifyOtpApi } from "../../utils/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { errorReaderToast } from "../../utils";
import carretLogo from "../../assets/carret-logo.png";
import Header from "../../Components/Header/Header";

const routeData = {
  otc: {
    route: "/otc",
    title: "OTC Admin",
  },
  fiu: {
    route: "/fiu",
    title: "FIU Admin",
  },
  compliance: {
    route: "/compliance?page=onboarding",
    title: "Compliance Admin",
  },
  internal: {
    route: "/internal?page=coin_dcx",
    title: "Internal Admin",
  },
  accounting: {
    route: "/accounting?page=transaction_order_history",
    title: "Accounting Admin",
  },
  "external-accounting": {
    route: "/external-accounting",
    title: "External Accounting Admin",
  },
  edd: {
    route: "/edd?page=edd_request",
    title: "EDD Admin",
  },
  transaction: {
    route: "/transaction?page=overview",
    title: "Transaction Admin",
  },
  taas: {
    route: "/taas",
    title: "Taas Admin",
  },
  kybOnboarding: {
    route: "/kybOnboarding?page=onboarding",
    title: "Kyb Onboarding Admin",
  },
  nonIndianKyb: {
    route: "/nonIndianKyb",
    title: "Non-Indian KYB Onboarding Admin",
  },
  otcNonIndia: {
    route: "/otcNonIndia?page=deposits",
    title: "OTC Non-India Admin",
  },
  apiDashboard: {
    route: "/apiDashboard",
    title: "API Dashboard Admin",
  },
  iplogs: {
    route: "/iplogs?page=documents",
    title: "IP Logs Admin",
  },
  ledger: {
    route: "/ledger?page=documents",
    title: "Ledger Admin",
  },
  kyc_review: {
    route: "/kyc_review",
    title: "KYC Review Admin",
  },
  "admin-2fa": {
    route: "/admin-2fa",
    title: "Admin 2FA Settings",
  },
};

const persistAuthTokens = (data) => {
  if (!data?.access_token) return false;

  localStorage.setItem("token", data.access_token);

  if (data?.refresh_token) {
    localStorage.setItem("refresh_token", data.refresh_token);
  }

  const expiresIn = data.expires_in || data.expires_ms || data.exp || null;
  if (expiresIn) {
    const expiryMs =
      Number(expiresIn) > 1e12
        ? Number(expiresIn)
        : Date.now() + Number(expiresIn) * 1000;
    localStorage.setItem("token_expiry", String(expiryMs));
  }

  return true;
};

const isLoginChallengeExpiredError = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return false;

  const messages = [];
  if (typeof data.detail === "string") messages.push(data.detail);
  if (typeof data.message === "string") messages.push(data.message);
  if (typeof data.error === "string") messages.push(data.error);
  if (Array.isArray(data.errors)) {
    data.errors.forEach((item) => {
      if (typeof item === "string") messages.push(item);
    });
  }

  return messages.some(
    (msg) =>
      /invalid or expired.*challenge/i.test(msg) ||
      /challenge.*(invalid|expired)/i.test(msg),
  );
};

const LoginLayout = ({ navigateTo }) => {
  const [loginLoader, setLoginLoader] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [email, setEmail] = useState();
  const [otp, setOtp] = useState();
  const [twoFaOtp, setTwoFaOtp] = useState();
  const [twoFaEnable, setTwoFaEnable] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [twoFaExpiryToastShown, setTwoFaExpiryToastShown] = useState(false);

  const navigate = useNavigate();
  const shouldShowTwoFaOtp = twoFaEnable || mfaRequired;

  useEffect(() => {
    if (shouldShowTwoFaOtp && !twoFaExpiryToastShown) {
      toast("Your 2FA OTP will expire within 10 minutes.", { icon: "ℹ️" });
      setTwoFaExpiryToastShown(true);
    }
  }, [shouldShowTwoFaOtp, twoFaExpiryToastShown]);

  const loginHandler = async (e) => {
    e.preventDefault();
    setLoginLoader(true);
    const nextEmail = e.target.email.value;
    setEmail(nextEmail);
    try {
      const { data } = await loginApi({ email: nextEmail });
      if (data?.is_2fa_enabled) setTwoFaEnable(true);
      toast.success("OTP sent to your email");
      setOtpSent(true);
    } catch (error) {
      errorReaderToast(error);
    } finally {
      setLoginLoader(false);
    }
  };

  const completeLogin = (data) => {
    if (!persistAuthTokens(data)) return;
    navigate(routeData[navigateTo]?.route);
  };

  const varifyOtpHandler = async () => {
    if (loginLoader) return;
    setLoginLoader(true);

    try {
      const { data } = mfaRequired
        ? await verify2faOtpApi({
            challenge_token: challengeToken,
            two_fa_otp: twoFaOtp,
          })
        : await verifyOtpApi({
            email,
            otp,
            ...(shouldShowTwoFaOtp && twoFaOtp
              ? { two_fa_otp: twoFaOtp }
              : {}),
          });

      if (data?.mfa_required) {
        setMfaRequired(true);
        setChallengeToken(data.challenge_token || "");
        setTwoFaOtp(undefined);
        return;
      }

      if (data?.access_token) {
        completeLogin(data);
      }
    } catch (error) {
      if (isLoginChallengeExpiredError(error)) {
        toast.error("Login challenge expired. Please sign in again.");
        setOtpSent(false);
        setOtp(undefined);
        setTwoFaOtp(undefined);
        setMfaRequired(false);
        setChallengeToken("");
        setTwoFaEnable(false);
        setTwoFaExpiryToastShown(false);
        return;
      }
      errorReaderToast(error);
    } finally {
      setLoginLoader(false);
    }
  };

  const canVerify = mfaRequired
    ? twoFaOtp?.length === 6
    : otp?.length === 6 &&
      (!shouldShowTwoFaOtp || twoFaOtp?.length === 6);

  return (
    <>
      <Header logoutEnable={false} />
      <div className="h-[60vh] flex flex-col justify-center items-center">
        <div>
          <img
            className="h-[5rem] mb-2 pr-2"
            src={carretLogo}
            alt="carret logo"
          ></img>
        </div>
        <p className="text-center text-2xl font-bold pb-2">
          {routeData[navigateTo]?.title}
        </p>
        {!otpSent ? (
          <form onSubmit={loginHandler}>
            <div className={styles.loginBox}>
              <div style={{ width: "100%" }}>
                <p>Enter Email</p>
                <Input placeholder="email" type="email" name="email" required />
              </div>
              <Button type="primary" htmlType="submit" loading={loginLoader}>
                Submit
              </Button>
            </div>
          </form>
        ) : (
          <div className={styles.loginBox}>
            {!mfaRequired && (
              <div style={{ width: "100%" }}>
                <p>Enter OTP sent to your email</p>
                <div style={{ width: "100%" }}>
                  <Input.OTP
                    style={{ width: "100%" }}
                    length={6}
                    value={otp}
                    onChange={setOtp}
                  />
                </div>
              </div>
            )}
            {shouldShowTwoFaOtp && (
              <div style={{ width: "100%" }}>
                <p>Enter 2FA OTP</p>
                <div style={{ width: "100%" }}>
                  <Input.OTP
                    style={{ width: "100%" }}
                    length={6}
                    value={twoFaOtp}
                    onChange={setTwoFaOtp}
                  />
                </div>
              </div>
            )}
            <Button
              type="primary"
              htmlType="submit"
              loading={loginLoader}
              onClick={varifyOtpHandler}
              disabled={!canVerify}
            >
              Verify
            </Button>
            <Button
              type="link"
              onClick={() => {
                setOtpSent(false);
                setOtp(undefined);
                setTwoFaOtp(undefined);
                setMfaRequired(false);
                setChallengeToken("");
                setTwoFaEnable(false);
                setTwoFaExpiryToastShown(false);
              }}
            >
              Use a different email
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default LoginLayout;
